import React, { FC, useEffect, useMemo } from 'react'
import { FormikState } from 'formik'
import { useLocation } from 'react-router-dom'

import AppCustomEditor from 'src/utils/CustomEditor'
import MediaPicker from './MediaPicker'
import { formatDateTime, timeAgo } from 'src/marketConnect/helpers'
import { CreateEditState } from './old-CreateProduct'
import { InitialValuesForCreateProduct } from 'src/interfaces/createProductInterface'

type FirstPhaseType = {
  values: InitialValuesForCreateProduct
  handleChange: (e: string | React.ChangeEvent<any>) => void
  setFormikState: (
    f:
      | FormikState<InitialValuesForCreateProduct>
      | ((
        prevState: FormikState<InitialValuesForCreateProduct>
      ) => FormikState<InitialValuesForCreateProduct>),
    cb?: (() => void) | undefined
  ) => void
  mode: 'create' | 'edit'
  getEditData: any
  productId?: number
}

type ProductMediaSectionProps = {
  values: InitialValuesForCreateProduct
  setFormikState: FirstPhaseType['setFormikState']
  productId?: number
  mode: 'create' | 'edit'
}

export const ProductMediaSection: FC<ProductMediaSectionProps> = ({
  values,
  setFormikState,
  productId,
  mode,
}) => (
  <div className="card card-flush">
    <div className="card-headerP">
      <div className="card-titleP">
        <h2>
          <label>Product Media</label>
        </h2>
      </div>
    </div>

    <div className="card-bodyP pt-0">
      <div className="fv-row">
        <MediaPicker values={values} setFormikState={setFormikState} maxImages={50} productId={productId} mode={mode} />
      </div>
    </div>
  </div>
)

const FirstPhase: FC<FirstPhaseType> = ({ values, handleChange, setFormikState, getEditData, productId, mode }) => {
  const location = useLocation()
  const state = location.state as CreateEditState | undefined

  // Keep behavior the same: prefer mode from route state if present.
  // const { mode } = useMemo(() => {
  //   return state || { mode: 'create', rowData: undefined }
  // }, [state])

  useEffect(() => {
    if (values?.isProductHasVariant === false) {
      setFormikState((prev: any) => {
        const current = prev.values.varients_data ?? [];

        // If already has a first row, do nothing (IMPORTANT for edit mode)
        if (current[0]) return prev;

        // If missing, create a first row (create + edit both)
        return {
          ...prev,
          values: {
            ...prev.values,
            varients_data: [{ sku: '', barcode: '', ean: '' }, ...current],
          },
        };
      });
    }
  }, [values?.isProductHasVariant, setFormikState]);

  return (
    <div>
      {/* Main column */}
      <div className="d-flex flex-column flex-row-fluid gap-4">
        {/* Tab content */}
        <div className="tab-content" data-select2-id="select2-data-135-s7h8">
          <div className="d-flex flex-column gap-4" data-select2-id="select2-data-134-eoln">
            {/* General Information Card */}
            <div className="card card-flush">
              <div className="card-headerP">
                <div className="card-titleP">
                  <h2>General Information</h2>
                </div>
              </div>

              <div className="card-bodyP pt-0">
                {/* Row 1: Product Name + Short Description */}
                <div className="row g-4 mb-4" style={{ marginTop: 'calc(-0.5 * var(--bs-gutter-y))' }}>
                  <div className="col-md-6">
                    <label className="required form-label">Product Name</label>
                    <input
                      type="text"
                      name="name"
                      className="form-control"
                      placeholder="e.g. Product-1"
                      value={values.name}
                      onChange={handleChange}
                    />
                    <div className="text-muted fs-7 mt-1">
                      A product name is required and recommended to be unique.
                    </div>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Short Description</label>
                    <input
                      type="text"
                      name="genralDescription"
                      className="form-control"
                      placeholder="Short tagline..."
                      value={values.genralDescription}
                      onChange={handleChange}
                    />
                    <div className="text-muted fs-7 mt-1">Brief summary shown in listings.</div>
                  </div>
                </div>

                {/* Row 2: SKU + Barcode + EAN (only when product has no variants) */}
                {!values?.isProductHasVariant && (
                  <>
                    <div className="row g-4 mb-4">
                      <div className="col-md-6">
                        <label className="form-label required">SKU</label>
                        <input
                          type="text"
                          name="sku"
                          className="form-control"
                          placeholder="Unique SKU"
                          value={values?.varients_data?.[0]?.sku ?? ""}
                          onChange={(e) => {
                            const val = e.target.value

                            setFormikState((prev: any) => {
                              const next = [...(prev.values.varients_data ?? [])]
                              next[0] = { ...(next[0] ?? {}), sku: val }

                              return {
                                ...prev,
                                values: { ...prev.values, varients_data: next },
                              }
                            })
                          }}
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">Barcode</label>
                        <input
                          type="text"
                          name="barcode"
                          className="form-control"
                          placeholder="Barcode"
                          value={values?.varients_data?.[0]?.barcode ?? ""}
                          onChange={(e) => {
                            const val = e.target.value

                            setFormikState((prev: any) => {
                              const next = [...(prev.values.varients_data ?? [])]
                              next[0] = { ...(next[0] ?? {}), barcode: val }

                              return {
                                ...prev,
                                values: { ...prev.values, varients_data: next },
                              }
                            })
                          }}
                        />
                      </div>
                    </div>

                    <div className="row g-4 mb-4">
                      <div className="col-md-6">
                        <label className="form-label">EAN</label>
                        <input
                          type="text"
                          name="ean"
                          className="form-control"
                          placeholder="European Article Number"
                          value={values?.varients_data?.[0]?.ean ?? ""}
                          onChange={(e) => {
                            const val = e.target.value

                            setFormikState((prev: any) => {
                              const next = [...(prev.values.varients_data ?? [])]
                              next[0] = { ...(next[0] ?? {}), ean: val }

                              return {
                                ...prev,
                                values: { ...prev.values, varients_data: next },
                              }
                            })
                          }}
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* Detailed Description (Editor) */}
                <div>
                  <label className="form-label">Detailed Description</label>
                  <AppCustomEditor
                    value={values.description}
                    onChangeData={(editorData) => {
                      setFormikState((prevState) => ({
                        ...prevState,
                        values: {
                          ...prevState.values,
                          description: editorData,
                        },
                      }))
                    }}
                  />
                </div>

                {/* Created / Last Modified (Edit only) */}
                {mode === 'edit' && (
                  <div className="mt-6">
                    <div className="row g-4">
                      <div className="col-md-6">
                        <div className="fw-semibold">Created At</div>
                        <div className="text-muted fs-7 mt-1">
                          {formatDateTime(getEditData?.api_created_at)}
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="fw-semibold">Last Modified</div>
                        <div className="text-muted fs-7 mt-1">{timeAgo(getEditData?.updated_at)}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* end::Tab content */}
      </div>
      {/* end::Main column */}
    </div>
  )
}

export default FirstPhase